import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
    ExternalComplianceValidationFinding,
    InternalSdsBuildRequest,
    InternalSdsBuildResponse,
    SdsLocale,
    SdsProfileId,
    SdsProfileRevision,
    SdsRevisionSummary,
} from "../../../data/models/schemas";
import type {
    AccidentalReleaseSection,
    ChemicalIngredient,
    CompositionSection,
    DisposalSection,
    EcologicalSection,
    ExposureControlsSection,
    FireFightingSection,
    FirstAidSection,
    HandlingStorageSection,
    HazardIdentificationSection,
    IdentificationSection,
    OtherInformationSection,
    PhysicalChemicalSection,
    RegulatorySection,
    SDSDocument,
    StabilityReactivitySection,
    ToxicologicalSection,
    TransportSection,
} from "../../../types/sds-types";
import {
    postBuildSds,
    postBuildSdsRealtime,
    type SdsBuildProgressUpdate,
} from "../../actions/sds";
import { resolveRsaaErrorMessage } from "../../utils/rsaaErrorMessage";
import { applyLocaleToSdsDocument } from "../../../components/sds/localeOptions";
import type { SdsActiveDocumentMeta } from "../../../components/sds/sdsManagement";
import {
    getSdsLocaleFromLanguageTag,
    getSdsLocaleLanguageTag,
} from "../../../components/sds/sdsApiContract";

export interface SdsState {
    sdsDocument?: SDSDocument;
    localizedDocuments: Partial<Record<SdsLocale, SDSDocument>>;
    findingsByLocale: Partial<
        Record<SdsLocale, ExternalComplianceValidationFinding[]>
    >;
    availableLocales: SdsLocale[];
    profileId?: SdsProfileId;
    profileRevision?: SdsProfileRevision;
    validationFindings: ExternalComplianceValidationFinding[];
    searchQuery?: InternalSdsBuildRequest;
    activeDocument: SdsActiveDocumentMeta | null;
    loading: boolean;
    progress: SdsBuildProgressUpdate | null;
    error: string | null;
}

const initialState: SdsState = {
    sdsDocument: null,
    localizedDocuments: {},
    findingsByLocale: {},
    availableLocales: [],
    profileId: undefined,
    profileRevision: undefined,
    validationFindings: [],
    searchQuery: null,
    activeDocument: null,
    loading: false,
    progress: null,
    error: null,
};

const createDraftMeta = (document?: SDSDocument): SdsActiveDocumentMeta => ({
    source: "build_draft",
    documentId: document?.documentId,
    name: undefined,
    status: undefined,
    currentRevisionNumber: undefined,
    loadedRevisionNumber: document?.revisionNumber
        ? Number(document.revisionNumber)
        : undefined,
    isPersisted: Boolean(document?.documentId),
    isHistoricalRevision: false,
    isDirty: false,
    revisionSummaries: [],
});

const createBuiltDraftMeta = (
    document?: SDSDocument
): SdsActiveDocumentMeta => ({
    ...createDraftMeta(document),
    documentId: undefined,
    isPersisted: false,
    isDirty: true,
});

const resetGeneratedMetadata = (state: SdsState) => {
    state.localizedDocuments = {};
    state.findingsByLocale = {};
    state.availableLocales = [];
    state.profileId = undefined;
    state.profileRevision = undefined;
    state.validationFindings = [];
};

const storeActiveLocalizedDocument = (state: SdsState) => {
    if (!state.sdsDocument) return;
    const locale = getSdsLocaleFromLanguageTag(state.sdsDocument.language);
    if (locale && state.localizedDocuments[locale]) {
        state.localizedDocuments[locale] = state.sdsDocument;
    }
};

const markDocumentDirty = (state: SdsState) => {
    if (!state.sdsDocument || state.activeDocument?.isHistoricalRevision) {
        return;
    }
    if (!state.activeDocument) {
        state.activeDocument = createDraftMeta(state.sdsDocument);
    }
    state.activeDocument.isDirty = true;
    storeActiveLocalizedDocument(state);
};

const updateSection = <Key extends keyof SDSDocument>(
    state: SdsState,
    section: Key,
    update: Partial<NonNullable<SDSDocument[Key]>>
) => {
    if (!state.sdsDocument) return;
    state.sdsDocument[section] = {
        ...(state.sdsDocument[section] as object),
        ...update,
    } as SDSDocument[Key];
    markDocumentDirty(state);
};

type IngredientIdentity = Pick<ChemicalIngredient, "name" | "casNumber">;

const normalizeIngredientIdentityField = (value?: string) => value || "";

const matchesIngredientIdentity = (
    name: string | undefined,
    casNumber: string | undefined,
    identity: IngredientIdentity
) =>
    normalizeIngredientIdentityField(name) ===
        normalizeIngredientIdentityField(identity.name) &&
    normalizeIngredientIdentityField(casNumber) ===
        normalizeIngredientIdentityField(identity.casNumber);

const syncIngredientIdentity = (
    document: SDSDocument,
    previousIdentity: IngredientIdentity,
    updatedIdentity: IngredientIdentity
) => {
    if (
        !normalizeIngredientIdentityField(previousIdentity.name) &&
        !normalizeIngredientIdentityField(previousIdentity.casNumber)
    ) {
        return;
    }

    document.exposureControls?.componentExposureLimits?.forEach((component) => {
        if (
            matchesIngredientIdentity(
                component.componentName,
                component.casNumber,
                previousIdentity
            )
        ) {
            component.componentName = updatedIdentity.name;
            component.casNumber = updatedIdentity.casNumber;
        }
    });

    document.toxicologicalInformation?.componentToxicology?.forEach(
        (component) => {
            if (
                matchesIngredientIdentity(
                    component.name,
                    component.casNumber,
                    previousIdentity
                )
            ) {
                component.name = updatedIdentity.name;
                component.casNumber = updatedIdentity.casNumber;
            }
        }
    );

    document.ecologicalInformation?.componentEcotoxicity?.forEach(
        (component) => {
            if (
                matchesIngredientIdentity(
                    component.name,
                    component.casNumber,
                    previousIdentity
                )
            ) {
                component.name = updatedIdentity.name;
                component.casNumber = updatedIdentity.casNumber;
            }
        }
    );

    document.regulatoryInformation?.ingredientRegulatory?.forEach(
        (component) => {
            if (
                matchesIngredientIdentity(
                    component.name,
                    component.casNumber,
                    previousIdentity
                )
            ) {
                component.name = updatedIdentity.name;
                component.casNumber = updatedIdentity.casNumber;
            }
        }
    );
};

const sdsSlice = createSlice({
    name: "sds",
    initialState,
    reducers: {
        setSDS(state, action: PayloadAction<SDSDocument>) {
            state.sdsDocument = action.payload;
            state.activeDocument = createDraftMeta(action.payload);
            resetGeneratedMetadata(state);
        },
        setManagedSds(
            state,
            action: PayloadAction<{
                document: SDSDocument;
                meta: SdsActiveDocumentMeta;
            }>
        ) {
            state.sdsDocument = action.payload.document;
            state.activeDocument = action.payload.meta;
            state.error = null;
            resetGeneratedMetadata(state);
        },
        clearSDS(state) {
            state.sdsDocument = null;
            state.activeDocument = null;
            state.progress = null;
            state.error = null;
            resetGeneratedMetadata(state);
        },
        setSearchQuery(state, action: PayloadAction<InternalSdsBuildRequest>) {
            state.searchQuery = action.payload;
        },
        clearSearchQuery(state) {
            state.searchQuery = null;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        updateDocumentMeta(
            state,
            action: PayloadAction<
                Partial<
                    Pick<
                        SDSDocument,
                        "revisionNumber" | "language" | "format" | "customId"
                    >
                >
            >
        ) {
            if (!state.sdsDocument) return;
            state.sdsDocument = {
                ...state.sdsDocument,
                ...action.payload,
            };
            markDocumentDirty(state);
        },
        syncDocumentLocale(
            state,
            action: PayloadAction<{ languageCode: string }>
        ) {
            if (!state.sdsDocument) return;
            const requestedLocale = getSdsLocaleFromLanguageTag(
                action.payload.languageCode
            );
            const generatedDocument = requestedLocale
                ? state.localizedDocuments[requestedLocale]
                : undefined;
            if (requestedLocale && generatedDocument) {
                storeActiveLocalizedDocument(state);
                state.sdsDocument = {
                    ...generatedDocument,
                    language: getSdsLocaleLanguageTag(requestedLocale),
                };
                state.validationFindings =
                    state.findingsByLocale[requestedLocale] ?? [];
                return;
            }
            if (state.availableLocales.length > 0) {
                return;
            }
            state.sdsDocument = applyLocaleToSdsDocument(
                state.sdsDocument,
                action.payload.languageCode
            );
            markDocumentDirty(state);
        },
        setRevisionSummaries(
            state,
            action: PayloadAction<SdsRevisionSummary[]>
        ) {
            if (!state.activeDocument) {
                state.activeDocument = createDraftMeta(state.sdsDocument);
            }
            state.activeDocument.revisionSummaries = action.payload;
        },
        updateIdentification(
            state,
            action: PayloadAction<Partial<IdentificationSection>>
        ) {
            updateSection(state, "identification", action.payload);
        },
        updateHazardIdentification(
            state,
            action: PayloadAction<Partial<HazardIdentificationSection>>
        ) {
            updateSection(state, "hazardIdentification", action.payload);
        },
        updateComposition(
            state,
            action: PayloadAction<Partial<CompositionSection>>
        ) {
            updateSection(state, "composition", action.payload);
        },
        updateCompositionIngredient(
            state,
            action: PayloadAction<{
                index: number;
                changes: Partial<ChemicalIngredient>;
            }>
        ) {
            const ingredients = state.sdsDocument?.composition?.ingredients;
            const ingredient = ingredients?.[action.payload.index];
            if (!state.sdsDocument || !ingredient) {
                return;
            }

            const previousIdentity: IngredientIdentity = {
                name: ingredient.name,
                casNumber: ingredient.casNumber,
            };

            Object.assign(ingredient, action.payload.changes);

            const updatedIdentity: IngredientIdentity = {
                name: ingredient.name,
                casNumber: ingredient.casNumber,
            };
            const identityChanged =
                normalizeIngredientIdentityField(previousIdentity.name) !==
                    normalizeIngredientIdentityField(updatedIdentity.name) ||
                normalizeIngredientIdentityField(previousIdentity.casNumber) !==
                    normalizeIngredientIdentityField(updatedIdentity.casNumber);

            if (identityChanged) {
                syncIngredientIdentity(
                    state.sdsDocument,
                    previousIdentity,
                    updatedIdentity
                );
            }

            markDocumentDirty(state);
        },
        updateFirstAidMeasures(
            state,
            action: PayloadAction<Partial<FirstAidSection>>
        ) {
            updateSection(state, "firstAidMeasures", action.payload);
        },
        updateFireFightingMeasures(
            state,
            action: PayloadAction<Partial<FireFightingSection>>
        ) {
            updateSection(state, "fireFightingMeasures", action.payload);
        },
        updateAccidentalReleaseMeasures(
            state,
            action: PayloadAction<Partial<AccidentalReleaseSection>>
        ) {
            updateSection(state, "accidentalReleaseMeasures", action.payload);
        },
        updateHandlingAndStorage(
            state,
            action: PayloadAction<Partial<HandlingStorageSection>>
        ) {
            updateSection(state, "handlingAndStorage", action.payload);
        },
        updateExposureControls(
            state,
            action: PayloadAction<Partial<ExposureControlsSection>>
        ) {
            updateSection(state, "exposureControls", action.payload);
        },
        updatePhysicalChemicalProperties(
            state,
            action: PayloadAction<Partial<PhysicalChemicalSection>>
        ) {
            updateSection(state, "physicalChemicalProperties", action.payload);
        },
        updateStabilityAndReactivity(
            state,
            action: PayloadAction<Partial<StabilityReactivitySection>>
        ) {
            updateSection(state, "stabilityAndReactivity", action.payload);
        },
        updateToxicologicalInformation(
            state,
            action: PayloadAction<Partial<ToxicologicalSection>>
        ) {
            updateSection(state, "toxicologicalInformation", action.payload);
        },
        updateEcologicalInformation(
            state,
            action: PayloadAction<Partial<EcologicalSection>>
        ) {
            updateSection(state, "ecologicalInformation", action.payload);
        },
        updateDisposalConsiderations(
            state,
            action: PayloadAction<Partial<DisposalSection>>
        ) {
            updateSection(state, "disposalConsiderations", action.payload);
        },
        updateTransportInformation(
            state,
            action: PayloadAction<Partial<TransportSection>>
        ) {
            updateSection(state, "transportInformation", action.payload);
        },
        updateRegulatoryInformation(
            state,
            action: PayloadAction<Partial<RegulatorySection>>
        ) {
            updateSection(state, "regulatoryInformation", action.payload);
        },
        updateOtherInformation(
            state,
            action: PayloadAction<Partial<OtherInformationSection>>
        ) {
            updateSection(state, "otherInformation", action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(postBuildSds.request, (state) => {
                state.loading = true;
                state.progress = null;
                state.error = null;
                state.sdsDocument = null;
                state.activeDocument = null;
                resetGeneratedMetadata(state);
            })
            .addCase(postBuildSdsRealtime.update, (state, action) => {
                state.progress = action.payload;
            })
            .addCase(
                postBuildSds.success,
                (state, action: PayloadAction<InternalSdsBuildResponse>) => {
                    state.loading = false;
                    state.progress = null;
                    const document: SDSDocument = action.payload.document;
                    const locale = getSdsLocaleFromLanguageTag(
                        document.language
                    );
                    state.sdsDocument = document;
                    state.localizedDocuments = locale
                        ? { [locale]: document }
                        : {};
                    state.findingsByLocale = locale
                        ? { [locale]: action.payload.validation.findings }
                        : {};
                    state.availableLocales = locale ? [locale] : [];
                    state.profileId = action.payload.profileId;
                    state.profileRevision = action.payload.profileRevision;
                    state.validationFindings =
                        action.payload.validation.findings;
                    state.activeDocument = createBuiltDraftMeta(document);
                }
            )
            .addCase(postBuildSds.error, (state, action) => {
                state.loading = false;
                state.progress = null;
                state.error = resolveRsaaErrorMessage(
                    action.payload,
                    "Failed to build SDS document."
                );
                state.sdsDocument = undefined;
                state.activeDocument = null;
                resetGeneratedMetadata(state);
            });
    },
});

export const {
    setSDS,
    setManagedSds,
    clearSDS,
    setSearchQuery,
    clearSearchQuery,
    setLoading,
    setError,
    setRevisionSummaries,
    updateIdentification,
    updateHazardIdentification,
    updateComposition,
    updateCompositionIngredient,
    updateFirstAidMeasures,
    updateFireFightingMeasures,
    updateAccidentalReleaseMeasures,
    updateHandlingAndStorage,
    updateExposureControls,
    updatePhysicalChemicalProperties,
    updateStabilityAndReactivity,
    updateToxicologicalInformation,
    updateEcologicalInformation,
    updateDisposalConsiderations,
    updateTransportInformation,
    updateRegulatoryInformation,
    updateOtherInformation,
    updateDocumentMeta,
    syncDocumentLocale,
} = sdsSlice.actions;

export default sdsSlice.reducer;
